import { FormikErrors, useFormikContext } from "formik";
import { TFunction } from "i18next";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { EventDetailsFormValues, EventFormValues } from "../../types/event";
import { EventDetailsStepFormFields } from "./EventDetailsStepFormFields";
import { EventFormCard } from "./EventFormCard";
import { getPrevLink } from "./eventStepperUtils";

export function getEventDetailsStepValidator(t: TFunction) {
	return (values: EventDetailsFormValues): FormikErrors<EventDetailsFormValues> => {
		const errors: FormikErrors<EventFormValues> = {};

		if (values.eventType?.formatRequirement === "required" && !values.format) {
			errors.format = t("Field is required");
		}
		if (values.eventType?.eventCategoryRequirement === "required" && !values.eventCategory) {
			errors.eventCategory = t("Field is required");
		}
		if (values.eventType?.industryRequirement === "required" && values.industries.length === 0) {
			errors.industries = t("Field is required");
		}
		if (values.eventType?.targetGroupRequirement === "required" && !values.targetGroup) {
			errors.targetGroup = t("Field is required");
		}
		if (values.eventType?.regionRequirement === "required" && !values.region) {
			errors.region = t("Field is required");
		}
		if (values.eventType?.primaryOrganizationalUnitRequirement === "required" && !values.primaryOrganizationalUnit) {
			errors.primaryOrganizationalUnit = t("Field is required");
		}
		if (values.eventType?.organizationalUnitRequirement === "required" && values.organizationalUnits.length === 0) {
			errors.organizationalUnits = t("Field is required");
		}
		if (values.eventType?.tagRequirement === "required" && values.tags.length === 0) {
			errors.tags = t("Field is required");
		}
		if (values.eventType?.preferenceRequirement === "required" && values.preferences.length === 0) {
			errors.preferences = t("Field is required");
		}
		if (values.eventType?.eventGoalRequirement === "required" && !values.eventGoal) {
			errors.eventGoal = t("Field is required");
		}

		return errors;
	};
}

export const isEventDetailsStepValid = (errors: FormikErrors<EventDetailsFormValues>) => {
	return (
		(!errors.translations ||
			(Array.isArray(errors.translations) &&
				!errors.translations.some((t) => typeof t === "object" && "additionalInfo" in t))) &&
		!errors.format &&
		!errors.eventCategory &&
		!errors.industries &&
		!errors.targetGroup &&
		!errors.region &&
		!errors.primaryOrganizationalUnit &&
		!errors.organizationalUnits &&
		!errors.tags &&
		!errors.eventGoal
	);
};

interface Props {
	variant?: "elevated" | "outlined";
}

export const EventDetailsStep: FC<Props> = ({ variant }) => {
	const { t } = useTranslation();
	const { values, errors, isValidating, setTouched, touched, submitForm } = useFormikContext<EventDetailsFormValues>();

	const handleSubmit = useCallback(async () => {
		await setTouched({
			...touched,
			format: true,
			eventCategory: true,
			industries: true as any,
			targetGroup: true,
			region: true,
			primaryOrganizationalUnit: true,
			organizationalUnits: true as any,
			tags: true as any,
			preferences: true as any,
			eventGoal: true,
			translations: values.translations.map(() => ({ additionalInfo: true as any })),
		});

		if (!isValidating && isEventDetailsStepValid(errors)) {
			await submitForm();
		}
	}, [errors, isValidating, setTouched, submitForm, touched, values.translations]);

	return (
		<EventFormCard
			variant={variant}
			title={t("Details")}
			prevLink={getPrevLink(values.eventType, "details")}
			onSubmitClick={handleSubmit}
			showLanguageSwitcher={false}
		>
			<EventDetailsStepFormFields />
		</EventFormCard>
	);
};
